import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Franco } from "@app/core/business/franco/franco";
import { Order } from "@app/core/business/order/order";
import { ContextService } from "@app/core/service/context.service";
import { FrancoStateService } from "@app/core/service/franco-state.service";
import { TranslateService } from "@ngx-translate/core";
import { untilDestroyed } from "ngx-take-until-destroy";
import { BehaviorSubject } from "rxjs";

@Component({
  selector: 'app-franco',
  templateUrl: './franco.component.html',
  styleUrls: ['./franco.component.scss'],
})
export class FrancoComponent implements OnInit, OnDestroy {
  @Input() public isFrancoUnique: boolean;

  @Input() public showFeeAmount: boolean = false;
  
  public contextOrder: Order;
  public hasFrancoConfig: boolean = false;
  public tooltipFrancoClassique: string;
  public tooltipFrancoUnique: string;

  public franco: Franco;
  
  public validityRate$ = new BehaviorSubject(0);
  public remainingTime$ = new BehaviorSubject('');

  constructor(
    public contextService: ContextService,
    public translateService: TranslateService,
    public francoState: FrancoStateService 
  ) {}


  ngOnDestroy(): void {
  }

  ngOnInit() {
    this.francoState.franco$.pipe(untilDestroyed(this)).subscribe(franco => {
      if (franco) {
        this.hasFrancoConfig = true;
        this.franco =  franco;
        this.updateTooltips(franco);
      }
    });

    this.contextService.fullContext$.pipe(untilDestroyed(this)).subscribe(context => {
      this.contextOrder = context.order;
      
      if (context.order) {
        this.francoState.refreshFranco(
          context.order,
          this.contextService.context.customerCode,
          this.contextService.context.companyCode
        );
      }
    });
  }

public hasMultiCompanyOrders(): boolean {
    if (!this.franco) return false;
    // On vérifie si les deux montants sont supérieurs à 0
    return this.franco.vitafraisTotalAmount > 0 && this.franco.pronaturaTotalAmount > 0;
  }

  /**
   * Logique pour griser le franco Classique (Société)
   */
public shouldDisableClassic(): boolean {
    if (!this.franco) return false;
    return this.franco.uniqueFrancoReached || this.hasMultiCompanyOrders();
  }

  /**
   * Logique pour griser le franco Unique (Commun)
   */
public shouldDisableUnique(): boolean {
    if (!this.franco) return false;
    
    // Si PAS de multi-société (une seule commande ou aucune), on grise le commun
    return !this.hasMultiCompanyOrders();
  }

 public getCurrentFee(): number {
    if (!this.franco) return 0;

    // Si le franco commun est prioritaire ou actif
    if (this.isFrancoUnique) {
      return this.franco.uniqueFrancoFee;
    }

    // Sinon taxe de la société actuelle
    return this.contextService.isVitafrais() 
      ? this.franco.vitafraisFrancoFee 
      : this.franco.pronaturaFrancoFee;
  }

  public shouldDisplayFees(): boolean {
    if (!this.franco) return false;

    // Vérification du franco de la société actuelle
    const isVtf = this.contextService.isVitafrais();
    const companyFrancoReached = isVtf 
      ? this.franco.vitafraisFrancoReached 
      : this.franco.pronaturaFrancoReached;

    // Vérification du franco commun
    const commonFrancoReached = this.franco.uniqueFrancoReached;

    // On n'affiche les frais que si aucun des deux n'est atteint
    return !companyFrancoReached && !commonFrancoReached;
  }

  private updateTooltips(franco: Franco) {
    // Unique
    this.tooltipFrancoUnique = `<div style="font-size: 3rem;">Franco unique: <b>${franco.uniqueFrancoMinimalOrderAmount.toFixed(2)}€</b></div>`;
    this.tooltipFrancoUnique += `<div style="font-size: 2rem; margin-top: 1rem;">Total des commandes <b>${this.franco.totalAmount.toFixed(2)}€</b></div>`;

    if(this.franco.uniqueFrancoAnnuleManuel)
      this.tooltipFrancoUnique += `<div style="font-size: 2rem; margin-top: 1rem;">(Franco offert)</div>`;

    
    // Classique
    let vtf = `<div style="font-size: 3rem;">Franco frais: <b>${franco.vitafraisFrancoMinimalOrderAmount.toFixed(2)}€</b></div>`;
    vtf += `<div style="font-size: 2rem; margin-top: 1rem;">Total des commandes Vitafrais <b>${this.franco.vitafraisTotalAmount.toFixed(2)}€</b></div>`;

    let pn = `<div style="font-size: 3rem;">Franco ProNatura: <b>${franco.pronaturaFrancoMinimalOrderAmount.toFixed(2)}€</b></div>`;
    pn += `<div style="font-size: 2rem; margin-top: 1rem;">Total des commandes Pronatura <b>${this.franco.pronaturaTotalAmount.toFixed(2)}€</b></div>`;

    this.tooltipFrancoClassique = this.contextService.isVitafrais() ? vtf : pn;

    if(this.contextService.isVitafrais() ? franco.vitafraisFrancoAnnuleManuel : franco.pronaturaFrancoAnnuleManuel)
      this.tooltipFrancoClassique += `<div style="font-size: 2rem; margin-top: 1rem;">(Franco offert)</div>`;

    if(!this.contextService.isVitafrais() && franco.pronaturaFrancoAnnuleEnlevement)
      this.tooltipFrancoClassique += `<div style="font-size: 2rem; margin-top: 1rem;">(Franco annulé pour enlèvement en magasin)</div>`;
}
}